import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import config from '../config/appConfig';
import '../styles/Auth.css';

// ── Inline password strength helper ───────────────────────────
const getStrength = pw => {
  if (!pw) return { score: 0, label: '', cls: '' };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { label: '', cls: '' },
    { label: 'Weak',   cls: 'strength-weak' },
    { label: 'Fair',   cls: 'strength-fair' },
    { label: 'Good',   cls: 'strength-good' },
    { label: 'Strong', cls: 'strength-strong' },
    { label: 'Strong', cls: 'strength-strong' },
  ];
  return { score, ...map[score] };
};

const Rule = ({ met, text }) => (
  <li className={met ? 'met' : ''}>{text}</li>
);

const UserRegister = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', password: '', confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const handleChange = e =>
    setFormData(f => ({ ...f, [e.target.name]: e.target.value }));

  const pw       = formData.password;
  const strength = getStrength(pw);
  const pctWidth = `${(strength.score / 5) * 100}%`;

  const handleSubmit = async e => {
    e.preventDefault();

    if (pw !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/auth/register/user', {
        firstName: formData.firstName,
        lastName:  formData.lastName,
        email:     formData.email,
        password:  formData.password
      });
      login(res.data.token, res.data.user, 'user');
      toast.success('Account created!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Create Account</h1>
        <p className="auth-subtitle">
          Register with {config.appName} to manage your health records
        </p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Jane"
              autoComplete="given-name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Smith"
              autoComplete="family-name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="jane@example.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onFocus={() => setShowRules(true)}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              required
              minLength={8}
            />
            {pw && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div
                    className={`strength-fill ${strength.cls}`}
                    style={{ width: pctWidth }}
                  />
                </div>
                <span className={`strength-label ${strength.cls}`}>
                  {strength.label}
                </span>
              </div>
            )}
            {showRules && (
              <ul className="password-rules">
                <Rule met={pw.length >= 8}       text="At least 8 characters" />
                <Rule met={/[A-Z]/.test(pw)}     text="One uppercase letter" />
                <Rule met={/[a-z]/.test(pw)}     text="One lowercase letter" />
                <Rule met={/[0-9]/.test(pw)}     text="One number" />
              </ul>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              autoComplete="new-password"
              required
            />
            {formData.confirmPassword && pw !== formData.confirmPassword && (
              <small style={{ color: '#c53030' }}>Passwords do not match</small>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || pw !== formData.confirmPassword}
          >
            {loading ? 'Creating Account…' : 'Create Account'}
          </button>
        </form>

        <div className="auth-links">
          <p>Already have an account? <Link to="/login/user">Sign in</Link></p>
          <p>Service provider? <Link to="/register/provider">Register here</Link></p>
        </div>
      </div>
    </div>
  );
};

export default UserRegister;
